import React, { useState, useEffect, useCallback } from 'react';
import './Dashboard.css';

const round1 = (v) => Math.round((v || 0) * 10) / 10;

const statut = (reel, objectif) => {
    if (objectif === 0) return 'neutral';
    return reel >= objectif ? 'ok' : 'exceed';
};

const StatCell = ({ reel, objectif }) => {
    const s = statut(reel, objectif);
    if (s === 'neutral') return <td className="db-val db-neutral">—</td>;
    return (
        // Lot 5.2 : les pastilles 🟢 / 🔴 sont devenues une puce FontAwesome. La
        // couleur vient de la classe `db-ok` / `db-exceed`, qui colore déjà le
        // nombre : la puce hérite de `currentColor`, l'information n'est donc plus
        // portée par un caractère mais par la classe — et le lecteur d'écran lit le
        // nombre au lieu d'annoncer « cercle vert ».
        <td className={`db-val ${s === 'ok' ? 'db-ok' : 'db-exceed'}`}>
            <i className="fa-solid fa-circle" style={{ fontSize: 8, marginRight: 5, verticalAlign: 'middle' }} aria-hidden="true" /> {round1(reel)}
        </td>
    );
};

/**
 * Tableau de bord du Planning — le tableau SEUL, sans en-tête.
 *
 * ── CE QUI A CHANGÉ AU LOT 5.5 ───────────────────────────────────────────────
 * Ce composant portait son propre en-tête (titre, « Rafraîchir », « Agrandir »),
 * affiché sauf `insideModal`. Or ses DEUX appelants le masquaient désormais : la
 * modale par `insideModal`, et la zone haute du Calendrier parce que la maquette
 * met ces boutons dans l'en-tête de la CARTE qui l'entoure. Le bloc n'était donc
 * plus rendu nulle part — et c'est lui qui portait les deux derniers `⬜` du
 * module, « Agrandir » écrit en emoji depuis l'origine.
 *
 * Il est retiré, avec les props `onOpenModal`, `onRefresh` et `insideModal` qui
 * n'existaient que pour lui. Les boutons vivent maintenant là où la maquette les
 * dessine : dans l'en-tête de la carte (`PlanningApp.js`).
 */
function DashboardCompact({ data }) {

    const [expanded, setExpanded] = useState({});

    useEffect(() => {
        if (!data) return;
        const init = {};
        data.forEach(item => { init[item.niveau.code] = true; });
        setExpanded(init);
    }, [data]);

    const toggleNiveau = (code) => {
        setExpanded(prev => ({ ...prev, [code]: !prev[code] }));
    };

    if (!data || data.length === 0) {
        return (
            <div className="db-compact">
                <div className="db-body">
                    <div className="db-empty"><i className="fa-solid fa-circle-info" /> Aucune donnée configurée</div>
                </div>
            </div>
        );
    }

    const parNiveau = {};
    data.forEach(item => {
        const k = item.niveau.code;
        if (!parNiveau[k]) parNiveau[k] = { niveau: item.niveau, items: [] };
        parNiveau[k].items.push(item);
    });

    return (
        <div className="db-compact">
            <div className="db-body">
                {Object.entries(parNiveau).map(([niveauCode, { niveau, items }]) => {
                    const isOpen    = expanded[niveauCode] !== false;
                    const types     = items[0]?.types || [];
                    const semestres = Object.keys(items[0]?.semestres || {}).sort();

                    return (
                        <div key={niveauCode} className="db-niveau-block">

                            <div className="db-niveau-header" onClick={() => toggleNiveau(niveauCode)}>
                                <span className="db-toggle"><i className={'fa-solid ' + (isOpen ? 'fa-chevron-down' : 'fa-chevron-right')} /></span>
                                <span className="db-niveau-title">{niveauCode}</span>
                                <span className="db-niveau-subtitle">{niveau.nom}</span>
                            </div>

                            {isOpen && (
                                <div className="db-table-scroll">
                                    <table className="db-table">
                                        <thead>
                                            <tr className="db-thead-top">
                                                <th className="db-th-label" rowSpan="2">Groupe</th>
                                                <th className="db-th-label" rowSpan="2">Type</th>
                                                <th className="db-th-annee" colSpan="2">∑ {semestres.join(' ')}</th>
                                                {semestres.map(sem => (
                                                    <th key={sem} className="db-th-sem" colSpan={2}>{sem}</th>
                                                ))}
                                            </tr>
                                            <tr className="db-thead-sub">
                                                <th className="db-th-sub">Planifié</th>
                                                <th className="db-th-sub">Objectif</th>
                                                {semestres.map(sem => (
                                                    <React.Fragment key={sem}>
                                                        <th className="db-th-sub">H. obj</th>
                                                        <th className="db-th-sub">J. obj</th>
                                                    </React.Fragment>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {items.map((item, iIdx) => {
                                                // Filtrer les types dont l'objectif > 0 sur au moins un semestre
                                                const typesVisibles = types.filter(type =>
                                                    semestres.some(sem =>
                                                        (item.semestres[sem]?.[type.code]?.heures_objectif || 0) > 0
                                                    )
                                                );

                                                return typesVisibles.map((type, tIdx) => {
                                                    const reel     = item.reel[type.code]?.jours || 0;
                                                    const objectif = item.objectif[type.code]?.jours || 0;
                                                    const isFirst  = tIdx === 0;
                                                    const isLast   = tIdx === typesVisibles.length - 1;

                                                    return (
                                                        <tr
                                                            key={`${iIdx}-${type.code}`}
                                                            className={`db-tr ${isLast ? 'db-tr-last' : ''}`}
                                                        >
                                                            {/* Groupe — affiché une seule fois en rowSpan */}
                                                            {isFirst && (
                                                                <td
                                                                    className="db-td-groupe"
                                                                    rowSpan={typesVisibles.length}
                                                                    style={{ borderLeft: `3px solid ${item.sous_niveau.couleur}` }}
                                                                >
                                                                    <span
                                                                        className="db-dot"
                                                                        style={{ background:
                                                                            typesVisibles.every(t =>
                                                                                statut(item.reel[t.code]?.jours || 0, item.objectif[t.code]?.jours || 0) !== 'exceed'
                                                                            ) ? '#16a34a' : '#dc2626'
                                                                        }}
                                                                    />
                                                                    {item.sous_niveau.nom}
                                                                </td>
                                                            )}

                                                            {/* Type */}
                                                            <td className="db-td-type">{type.libelle}</td>

                                                            {/* Planifié vs Objectif annuel */}
                                                            <StatCell reel={reel} objectif={objectif} />
                                                            <td className="db-td-obj">{round1(objectif)}</td>

                                                            {/* Détail par semestre */}
                                                            {semestres.map(sem => {
                                                                const d = item.semestres[sem]?.[type.code];
                                                                return (
                                                                    <React.Fragment key={sem}>
                                                                        <td className="db-td-num">{round1(d?.heures_objectif)}</td>
                                                                        <td className="db-td-num">{round1(d?.jours_objectif)}</td>
                                                                    </React.Fragment>
                                                                );
                                                            })}
                                                        </tr>
                                                    );
                                                });
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default DashboardCompact;
